/**
 * Error Normalization Utility
 * Converts API errors to user-friendly messages with context
 */

export const normalizeError = (error, context = 'operation') => {
  if (!error) return `Failed to complete ${context}`;

  // Handle API errors with status codes
  if (error.status) {
    switch (error.status) {
      case 400:
        return error.data?.message || `Invalid ${context} data`;
      case 401:
        return 'Your session has expired. Please login again.';
      case 403:
        return `You do not have permission to ${context}`;
      case 404:
        return `${context} not found`;
      case 409:
        return error.data?.message || `${context} already exists`;
      case 422:
        return error.data?.message || `Invalid ${context} information`;
      case 500:
        return 'Server error. Please try again later.';
      case 503:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return error.data?.message || `Failed to ${context}`;
    }
  }

  // Handle network errors
  if (error.message === 'Failed to fetch') {
    return 'Network error. Please check your connection.';
  }

  // Handle custom error messages
  if (error.message) {
    return error.message;
  }

  return `Failed to complete ${context}`;
};

/**
 * Retry logic with exponential backoff
 */
export const withRetry = async (fn, maxRetries = 3, baseDelay = 1000) => {
  let lastError;

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;

      // Don't retry on client errors (4xx)
      if (err.status && err.status >= 400 && err.status < 500) {
        throw err;
      }

      // Don't retry on last attempt
      if (i === maxRetries - 1) {
        throw err;
      }

      // Exponential backoff
      const delay = baseDelay * Math.pow(2, i);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError;
};
